import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { Progress } from "./ui/progress";
import { useLanguage } from "../contexts/LanguageContext";

export default function LoadingScreen() {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentTip, setCurrentTip] = useState(0);

  const loadingSteps = [
    t('loadingStep1'),
    t('loadingStep2'),
    t('loadingStep3'),
    t('loadingStep4'),
    t('loadingStep5')
  ];

  const tips = [
    "73% of employees wear company merch regularly.",
    "Branded merchandise increases brand recognition by 85%.",
    "Custom merch can boost customer loyalty by up to 40%.",
    "The global promotional products industry is worth $24 billion.",
    "People keep promotional products for an average of 8 months."
  ];

  useEffect(() => {
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return 95;
        return prev + Math.random() * 3 + 1;
      });
    }, 500);

    // Step animation
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= loadingSteps.length - 1) return prev;
        return prev + 1;
      });
    }, 8000);

    // Tip rotation
    const tipInterval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % tips.length);
    }, 5000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
      clearInterval(tipInterval);
    };
  }, [loadingSteps.length, tips.length]);

  return (
    <div 
      className="min-h-screen bg-white flex flex-col items-center justify-center px-6"
      data-testid="loading-screen"
    >
      <div className="max-w-md w-full text-center">
        {/* Animated Logo */}
        <motion.div
          className="w-24 h-24 mx-auto mb-10 relative"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-blue-600 rounded-3xl animate-pulse-glow" />
          <div className="absolute inset-2 bg-white rounded-2xl flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-purple-600" />
          </div>
        </motion.div>

        {/* Current Step */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <h2 className="font-display text-2xl font-semibold text-gray-900 mb-2">
              {loadingSteps[currentStep]}
            </h2>
          </motion.div>
        </AnimatePresence>

        {/* Progress Bar */}
        <div className="mb-6">
          <Progress value={progress} className="h-2 bg-gray-100" />
          <div className="flex justify-between mt-2 text-sm text-gray-500">
            <span>{Math.round(progress)}%</span>
            <span>{t('loadingTime')}</span>
          </div>
        </div>

        {/* Step Indicators */}
        <div className="flex justify-center gap-2 mb-10">
          {loadingSteps.map((_, index) => (
            <motion.div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                index <= currentStep ? "bg-purple-600" : "bg-gray-200"
              }`}
              animate={index === currentStep ? { scale: [1, 1.3, 1] } : {}}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
          ))}
        </div>

        {/* Tips */}
        <div className="bg-purple-50 rounded-xl p-6">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentTip}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-sm text-purple-700"
            >
              <span className="font-medium">Did you know?</span>{" "}
              {tips[currentTip]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Animated dots */}
        <div className="flex justify-center gap-1 mt-8">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 bg-gray-300 rounded-full"
              animate={{ y: [0, -8, 0] }}
              transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: i * 0.15
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
